import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { EventsStoreContext } from '../reducers/Events/EventsContext';
import PointsSlider from '../components/PointsSlider';
import PerPointRender from './PerPointRender';
import moment from 'moment';
import _ from 'lodash';
import { Howl } from 'howler';
import AppMessage from './AppMessage';

let beepInterval = null;

const beep = () => {
  const sound = new Howl({
    src: ['./call.mp3'],
  });
  sound.play();
};

const prepareActiveEvents = (newState, query) => {
  const state = _.cloneDeep(newState);
  const pointEvents = {};
  for (const eventId in state.activeEvents) {
    const { pointId, orderDate, assignee } = state.activeEvents[eventId];

    const pointInitialized = Array.isArray(pointEvents[pointId]);

    let date = orderDate;
    if (pointInitialized && moment(pointEvents[pointId]?.date).isBefore(moment(orderDate))) {
      date = pointEvents[pointId].date;
    }

    if (pointEvents[pointId]) {
      pointEvents[pointId].date = date;
      pointEvents[pointId].requestedServices = Array.from(
        new Set([
          ...pointEvents[pointId].requestedServices,
          ...Object.keys(state.activeEvents[eventId].services),
        ]),
      );
      pointEvents[pointId].events.push(state.activeEvents[eventId]);
    } else {
      pointEvents[pointId] = {};
      pointEvents[pointId].date = orderDate;
      pointEvents[pointId].pointId = pointId;
      pointEvents[pointId].eventIds = [];
      pointEvents[pointId].assignee = assignee ?? null;
      pointEvents[pointId].requestedServices = Object.keys(state.activeEvents[eventId].services);
      pointEvents[pointId].events = [state.activeEvents[eventId]];
    }
  }

  for (const pointId in state.calls) {
    let callDate = null;
    let assignee = null;
    for (const call of state.calls[pointId]) {
      const newCallDate = call?.createdAt;
      const isBefore = callDate && moment(callDate).isBefore(moment(newCallDate));
      if (isBefore || !callDate) {
        callDate = newCallDate;
      }
      if (call.assignee) {
        assignee = call.assignee;
      }
    }

    const pointInitialized = Array.isArray(pointEvents[pointId]);
    let date = pointInitialized ? pointEvents[pointId]?.date : callDate;
    if (pointInitialized && moment(date).isAfter(moment(callDate))) {
      date = callDate;
    }

    const calls = state.calls[pointId];

    if (pointEvents[pointId]) {
      pointEvents[pointId].date = date;
      pointEvents[pointId].events.push(...calls);
    } else {
      pointEvents[pointId] = {};
      pointEvents[pointId].date = date;
      pointEvents[pointId].pointId = pointId;
      pointEvents[pointId].eventIds = [];
      pointEvents[pointId].assignee = assignee ?? null;
      pointEvents[pointId].requestedServices = [];
      pointEvents[pointId].events = [...calls];
    }
  }

  const unorderedPoints = [];
  for (const pointId in pointEvents) {
    unorderedPoints.push(pointEvents[pointId]);
  }

  const queuedPoints = unorderedPoints
    .sort((event1, event2) => {
      if (!event1.date || !event2.date) {
        console.error('ERROR NO DATE', event1, event2);
      }
      const comparison = moment(event1.date).valueOf() - moment(event2.date).valueOf();
      return comparison;
    })
    .map((pointData) => ({
      pointId: pointData.pointId,
      assignee: pointData.assignee,
      type: pointData?.events?.[0]?.details?.delivery?.type ? pointData?.events?.[0]?.details?.delivery?.type : 'default',
    }));

  const activePoint = query ? query : queuedPoints?.length > 0 ? queuedPoints[0]?.pointId : null;
  return { queuedPoints, pointEvents, activePoint };
};

function useQuery() {
  const { search } = useLocation();
  const point = search?.split('=')[1] || undefined;

  return React.useMemo(() => point, [search]);
}

const PerPointManagement = () => {
  const [eventsState] = useContext(EventsStoreContext);
  const query = useQuery();
  const history = useHistory();
  const { queuedPoints, pointEvents, activePoint } = prepareActiveEvents(eventsState, query);
  const [selectedPoint, setSelectedPoint] = useState(activePoint);
  const [beeping, setBeeping] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      if (beepInterval !== null) {
        clearInterval(beepInterval);
        beepInterval = null;
      }
    };
  }, []);

  useEffect(() => {
    if ((selectedPoint === null && activePoint !== null) || activePoint !== selectedPoint) {
      setSelectedPoint(query ? query : activePoint);
    }

    const beepingActiveEvents = queuedPoints.filter((event) => {
      return event.assignee === null;
    });
    beepingActiveEvents.length > 0 ? setBeeping(true) : setBeeping(false);
  }, [eventsState, query]);

  useEffect(() => {
    if (!beeping) {
      beepInterval !== null && clearInterval(beepInterval);
      beepInterval = null;
    } else if (beepInterval === null) {
      beep();
      beepInterval = setInterval(beep, 5000);
    }
  }, [beeping]);

  useEffect(() => {
    !query &&
      query !== 'null' &&
      selectedPoint &&
      history.replace(encodeURI(`/live?point=${activePoint}`));
  }, [selectedPoint]);

  const handlePointClick = (newPoint) => {
    newPoint !== selectedPoint && history.push(encodeURI(`/live?point=${newPoint}`));
    setSelectedPoint(newPoint);
  };

  if (Object.keys(pointEvents).length === 0) {
    return <AppMessage message="No events have been requested for the moment" />;
  }

  return (
    <div className="p-rel f-c f-dirc">
      <div className="p-fix p-top bd-b2 bg-white-o l1" style={{ top: 64 }}>
        <PointsSlider
          points={queuedPoints}
          activePoint={selectedPoint}
          setActivePoint={handlePointClick}
        />
      </div>
      <PerPointRender pointEvents={pointEvents[selectedPoint]} />
      <div className="minH200" />
    </div>
  );
};

export default PerPointManagement;
